import { createSelector } from "reselect"
import { chain, filter, some, first, get, map, sumBy, size, capitalize, find, pick } from "lodash"
import { formatLabel, getTickerAndMicFromContributor, compareId } from "helpers/securities"
import { OK } from "helpers/data-statuses"
import { buildContributorKey, combinePositionsIncludingSplitBasket } from "helpers/contributors"
import { getCurrentConstraints } from "./constraints"
import { getPortfolioEquity, getRawPortfolioEquity, getModuleRiskTarget, getCoverage } from "./data"
import { getStatus as getScannerStatus } from "modules/optimization-scanner/selectors/data"
import { getSelectedModelId } from "modules/portfolio-root/selectors/portfolios"
import { getOptimizeDate } from "."
import { computeExperiment } from "helpers/experiments"

function getSecurityTradeType(original, delta) {
  if (original > 0) {
    if (delta > 0) {
      return "buy"
    } else {
      return "sell"
    }
  } else if (original < 0) {
    if (delta > 0) {
      return "cover"
    } else {
      return "short"
    }
  } else if (delta > 0) { // original === 0
    return "buy"
  } else {
    return "short"
  }
}

function formatTrade({ ticker, assetClass, mic, country, originalSecurities, optimized, equity, original, type, id }) {
  return {
    id,
    key: `${ticker}.${mic}`,
    ticker,
    mic,
    assetClass,
    country,
    currency: get(first(originalSecurities), "currency"),
    classification: get(first(originalSecurities), "classification.name"),
    label: formatLabel({ ticker, country }),
    tradeType: getSecurityTradeType(original, optimized),
    percentEquity: optimized / equity,
    originalPercentEquity: original / equity,
    rebalancedPercentEquity: (original + optimized) / equity,
    economicExposure: original + optimized,
    type,
  }
}

export const getRawTrades = state => get(state, "global.data.scorecardData.data.model.optimization.positionsDelta.0")
const getOptimizedPositions = state => get(state, "global.data.scorecardData.data.model.optimization.positions.dates[0]")
const getRawPositions = state => get(state, "global.data.portfolioData.data.model.simulation.composition.0.positions")

// Partially modeled baskets will be split in the composition query
// Since you cannot trade on a specific part of the partially modeled basket, we recombine them into a single position
const getPositions = createSelector(
  [getRawPositions],
  (positions) => combinePositionsIncludingSplitBasket(map(positions, (position) => ({
    ...getTickerAndMicFromContributor(position),
    ...position,
  })),
  ),
)

export const getTrades = createSelector(
  [getRawTrades, getPositions, getPortfolioEquity],
  (trades, positions, equity) => {
    if (!positions) {
      return null
    }

    return chain(trades)
      .map((tradedPositions, type) => map(tradedPositions, (tradedPosition) => {
        const { economicExposure: tradeEconomicExposure, id } = tradedPosition
        let original
        let securityId = id

        // there are only baskets and equities in trades
        // baskets
        if (type === "swaps") {
          securityId = getTickerAndMicFromContributor({ id: securityId, assetClass: "swap" })
          original = find(positions, ({ id: positionId }) => id === positionId)
        // equities
        } else {
          original = find(positions, (p) => buildContributorKey(p) === buildContributorKey(id))
        }
        original = {
          ...original,
          ...securityId,
        }
        const { ticker, mic } = original
        return {
          ...original,
          key: `${ticker}.${mic}`,
          label: formatLabel(original),
          tradeType: getSecurityTradeType(original.economicExposure, tradeEconomicExposure),
          percentEquity: tradeEconomicExposure / equity,
          originalPercentEquity: original.economicExposure / equity,
          rebalancedPercentEquity: (original.economicExposure + tradeEconomicExposure) / equity,
          // When active, the economicExposure will be the active weight + the trade
          economicExposure: original.economicExposure + tradeEconomicExposure,
          // Non active value of the position set + the trade
          positionSetEconomicExposure: original.positionSetEconomicExposure + tradeEconomicExposure,
          type,
        }
      }),
      )
      .flatten()
      .filter(trade => Math.abs(trade.originalPercentEquity - trade.rebalancedPercentEquity) >= 0.00005)
      .value()
  },
)

const getAllModeledPositions = createSelector(
  [getOptimizedPositions, getCoverage],
  (optimizedPositions, coverage) => chain(optimizedPositions)
    .omit("otherAssets")
    .mapValues((securities, type) =>
      filter(securities, optimizedPosition => {
        let isUnmodeled = false

        switch (type) {
          case "equities":
            isUnmodeled = chain(coverage).get("missingEquities").some(security => compareId(security.id, optimizedPosition.id)).value()
            break
          case "fixedIncome":
            isUnmodeled = chain(coverage).get("missingFixedIncome").some(security => security.id.isin, optimizedPosition.id.isin).value()
            break
          default:
            isUnmodeled = chain(coverage).get(`missing${capitalize(type)}`).some(security => security.id === optimizedPosition.id).value()
        }
        return !isUnmodeled
      }),
    )
    .value(),
)

export const getAllPositions = createSelector(
  [getTrades, getAllModeledPositions, getPortfolioEquity],
  (trades, modeledPositions, equity) =>
    [...(trades || []),
      ...chain(modeledPositions)
        .flatMap((perAssetClassPositions, type) =>
          map(perAssetClassPositions, ({ id, bloombergTicker }) => {
            // id will be an object for equities but only a string for swaps
            // { ticker, mic, modelProviderId, bloombergTicker }
            let originalSecurities = []
            let original = 0
            let securityId = id
            let country
            let assetClass

            // Remove positions that are already in the trades
            // only swaps and equities can be in trades
            const positionTraded = some(trades, elem => {
              const position = type === "swaps" ? { id, assetClass: "swap" } : { ...id, assetClass: "equity" }
              return buildContributorKey(elem) === buildContributorKey(position)
            })
            if (positionTraded) {
              return null
            }

            if (type === "equities") {
              originalSecurities = filter(modeledPositions.equities, ({ id: positionId }) => compareId(id, positionId))
              original = sumBy(originalSecurities, "economicExposure", 0)
              country = get(first(originalSecurities), "country")
            } else {
              switch (type) {
                case "swaps":
                  assetClass = "swap"
                  securityId = getTickerAndMicFromContributor({ id: securityId, assetClass })
                  break
                case "currencies":
                  assetClass = "currency"
                  securityId = getTickerAndMicFromContributor({ id: securityId, assetClass })
                  break
                case "fixedIncome":
                  assetClass = "fixedIncome"
                  securityId = getTickerAndMicFromContributor({ id: securityId, assetClass })
                  break
                case "commodities":
                  assetClass = "commodity"
                  securityId = getTickerAndMicFromContributor({ id: securityId, assetClass })
                  break
                case "otherAssets":
                  assetClass = "otherAsset"
                  securityId = getTickerAndMicFromContributor({ id: securityId, assetClass })
                  break
              }

              originalSecurities = filter(modeledPositions[type], ({ id: positionId }) => id === positionId)
              original = sumBy(originalSecurities, "economicExposure", 0)
            }

            return formatTrade({
              id,
              assetClass,
              ticker: securityId.ticker,
              mic: securityId.mic,
              country,
              bloombergTicker,
              originalSecurities,
              optimized: 0, // It's not in the trades, so it's kept as is, this represents the delta
              equity,
              original,
              type,
            })
          }),
        )
        .filter(elem => elem)
        .value()],
)

export const getRebalanceTrades = createSelector(
  [getTrades],
  (trades) => map(trades, position => ({
    ...pick(position, ["id", "ticker", "mic", "modelProviderId", "assetClass", "country", "internalId", "bloombergTicker"]),
    optimized: position.rebalancedPercentEquity,
    original: position.originalPercentEquity,
  })),
)

export const getExperiment = createSelector(
  [getScannerStatus, getCurrentConstraints, getModuleRiskTarget, getTrades, getPositions, getRawPortfolioEquity, getOptimizeDate, getSelectedModelId],
  (scannerStatus, constraints, riskTarget, trades, positions, portfolioEquity, selectedDate, selectedModelId) => {
    if (scannerStatus !== OK || !positions) {
      return null
    }

    if (size(trades) !== 0) {
      positions = chain(positions)
        .omit(["date", "equity", "gmv"])
      // If we find the position in the trades, we use the trade, otherwise we use the portfolio position
        .map((position) => find(trades, trade => buildContributorKey(trade) === buildContributorKey(position)) || position)
        .value()
    }

    positions = map(positions, position => ({
      ...position,
      economicExposure: position.positionSetEconomicExposure,
    }))

    return {
      config: {
        constraints,
        riskTarget,
      },
      type: "OPTIMIZATION",
      modelId: selectedModelId,
      positionSet: {
        ...computeExperiment(positions),
        date: selectedDate,
        equity: portfolioEquity,
      },
    }
  },
)
